import React from 'react';
import { OUTPUT_PRESETS } from '../../config';
import type { OutputSettings, Resolution, FrameRate } from '../../types';

interface OutputSettingsProps {
  settings: OutputSettings;
  onChange: (settings: OutputSettings) => void;
}

const RESOLUTION_OPTIONS: { label: string; value: Resolution }[] = [
  { label: '1920 × 1080 — Full HD', value: '1920x1080' },
  { label: '1280 × 720  — HD',      value: '1280x720'  },
  { label: '1080 × 1920 — Shorts',  value: '1080x1920' },
];

const FPS_OPTIONS: { label: string; value: FrameRate }[] = [
  { label: '30 fps', value: 30 },
  { label: '60 fps', value: 60 },
];

const BITRATE_OPTIONS = [
  { label: '4 Mbps — Web', value: '4M' },
  { label: '8 Mbps — Broadcast', value: '8M' },
  { label: '16 Mbps — Master', value: '16M' },
];

const PRESET_SHORTCUTS = [
  { label: 'HD 30', settings: OUTPUT_PRESETS.HD_30 },
  { label: 'HD 60', settings: OUTPUT_PRESETS.HD_60 },
  { label: 'SD',    settings: OUTPUT_PRESETS.SD_30 },
  { label: 'Shorts',settings: OUTPUT_PRESETS.SHORTS_30 },
];

export function OutputSettings({ settings, onChange }: OutputSettingsProps) {
  function set<K extends keyof OutputSettings>(key: K, value: OutputSettings[K]) {
    onChange({ ...settings, [key]: value });
  }

  return (
    <div className="space-y-4 p-4">
      <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Output</h3>

      {/* Preset quick-select */}
      <div>
        <label className="text-xs text-zinc-500 mb-1.5 block">Presets</label>
        <div className="grid grid-cols-4 gap-1">
          {PRESET_SHORTCUTS.map(({ label, settings: preset }) => {
            const active =
              preset.resolution === settings.resolution &&
              preset.fps === settings.fps &&
              preset.bitrate === settings.bitrate;
            return (
              <button
                key={label}
                type="button"
                onClick={() => onChange(preset)}
                className={[
                  'py-1 rounded text-[10px] font-medium transition-colors',
                  active
                    ? 'bg-blue-600 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600',
                ].join(' ')}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Resolution */}
      <Field label="Resolution">
        <Select
          value={settings.resolution}
          onChange={(v) => set('resolution', v as Resolution)}
          options={RESOLUTION_OPTIONS}
        />
      </Field>

      {/* Frame rate */}
      <Field label="Frame Rate">
        <div className="flex gap-1.5">
          {FPS_OPTIONS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => set('fps', value)}
              className={[
                'flex-1 py-1.5 rounded text-xs font-medium transition-colors',
                settings.fps === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
      </Field>

      {/* Bitrate */}
      <Field label="Bitrate">
        <Select
          value={settings.bitrate}
          onChange={(v) => set('bitrate', v)}
          options={BITRATE_OPTIONS}
        />
      </Field>

      {/* Summary chip */}
      <div className="rounded-md bg-zinc-800 px-3 py-2 text-[10px] font-mono text-zinc-400 leading-relaxed">
        <span className="text-zinc-300">{settings.resolution}</span>
        {' · '}
        <span className="text-zinc-300">{settings.fps}fps</span>
        {' · '}
        <span className="text-zinc-300">{settings.bitrate}</span>
        {' · H.264 MP4'}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs text-zinc-400">{label}</label>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md bg-zinc-800 border border-zinc-600 px-2.5 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 transition-colors"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
